import React, { useCallback, useEffect, useRef, useState, RefObject } from 'react';

const SCROLL_STEP = 40;

interface UseScrollSyncOptions {
  tableWrapperRef: RefObject<HTMLDivElement>;
  hasHorizontalOverflow: boolean;
}

interface UseScrollSyncReturn {
  topScrollbarRef: RefObject<HTMLDivElement>;
  topScrollbarInnerRef: RefObject<HTMLDivElement>;
  showTopScrollbar: boolean;
  handleKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
}

export const useScrollSync = ({
  tableWrapperRef,
  hasHorizontalOverflow,
}: UseScrollSyncOptions): UseScrollSyncReturn => {
  const topScrollbarRef = useRef<HTMLDivElement>(null);
  const topScrollbarInnerRef = useRef<HTMLDivElement>(null);
  const [showTopScrollbar, setShowTopScrollbar] = useState(false);
  const isSyncing = useRef(false);

  useEffect(() => {
    if (!hasHorizontalOverflow) {
      setShowTopScrollbar(false);
      return;
    }

    const tableWrapper = tableWrapperRef.current;
    if (!tableWrapper) return;

    const intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        setShowTopScrollbar(!entry.isIntersecting);
      },
      { threshold: 1.0 },
    );

    intersectionObserver.observe(tableWrapper);

    return () => intersectionObserver.disconnect();
  }, [tableWrapperRef, hasHorizontalOverflow]);

  useEffect(() => {
    const tableWrapper = tableWrapperRef.current;
    const scrollbarInner = topScrollbarInnerRef.current;
    const topScrollbar = topScrollbarRef.current;
    if (!tableWrapper || !scrollbarInner || !topScrollbar || !showTopScrollbar) return;

    let rafId: number | null = null;
    const syncWidth = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        scrollbarInner.style.width = `${tableWrapper.scrollWidth}px`;
        rafId = null;
      });
    };

    scrollbarInner.style.width = `${tableWrapper.scrollWidth}px`;

    const resizeObserver = new ResizeObserver(syncWidth);
    resizeObserver.observe(tableWrapper);

    const table = tableWrapper.querySelector('table');
    if (table) resizeObserver.observe(table);

    topScrollbar.scrollLeft = tableWrapper.scrollLeft;

    const onTableScroll = () => {
      if (isSyncing.current) return;
      isSyncing.current = true;
      topScrollbar.scrollLeft = tableWrapper.scrollLeft;
      requestAnimationFrame(() => {
        isSyncing.current = false;
      });
    };

    const onTopScroll = () => {
      if (isSyncing.current) return;
      isSyncing.current = true;
      tableWrapper.scrollLeft = topScrollbar.scrollLeft;
      requestAnimationFrame(() => {
        isSyncing.current = false;
      });
    };

    tableWrapper.addEventListener('scroll', onTableScroll, { passive: true });
    topScrollbar.addEventListener('scroll', onTopScroll, { passive: true });

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      tableWrapper.removeEventListener('scroll', onTableScroll);
      topScrollbar.removeEventListener('scroll', onTopScroll);
    };
  }, [tableWrapperRef, showTopScrollbar]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        const tableWrapper = tableWrapperRef.current;
        if (!tableWrapper) return;
        tableWrapper.scrollBy({
          left: event.key === 'ArrowLeft' ? -SCROLL_STEP : SCROLL_STEP,
          behavior: 'smooth',
        });
      }
    },
    [tableWrapperRef],
  );

  return {
    topScrollbarRef,
    topScrollbarInnerRef,
    showTopScrollbar,
    handleKeyDown,
  };
};
